import { EventListItem } from '@domain/event';
import { useGenericInfiniteQuery } from '@hooks/use-generic-infinite-query';
import { useGenericQuery } from '@hooks/use-generic-query';
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { paramsSerializer } from '@utils/format-params';
import { Params } from '@utils/format-params/format-params.type';
import { request } from '@utils/request/request';
import { RequestError, RequestMethod } from '@utils/request/request.type';

import { QUERY_KEYS } from './constants.type';
import type { EventListRequestParams, GetEventListHookParams } from './use-get-event-list.type';

const getEventList = async ({ companyId, clubId, params }: EventListRequestParams) => {
  const queryParams = paramsSerializer(params as Params);
  const endpoint = `/v1/nightgraph/companies/${companyId}/clubs/${clubId}/events?${queryParams}`;
  const url = `${import.meta.env.VITE_EVENTS_HOST}${endpoint}`;
  return request({ url, method: RequestMethod.GET });
};
export const useGetEventList = ({
  companyId,
  params,
  options,
  clubId,
  view,
}: GetEventListHookParams): UseInfiniteQueryResult<InfiniteData<EventListItem[], number>, RequestError> => {
  const queryParams = [
    `${QUERY_KEYS.eventList}-${view}`,
    companyId,
    clubId,
    params.search,
    params.sort,
    params.orderBy,
    params.startingWeekDays,
    params.endingTimeFrom,
    params.endingTimeUntil,
    params.channels,
    params.startTime,
    params.endTime,
    // Part of the key because it bounds the rows returned: without it a consumer
    // asking for 100 can be served another's cached page of 10.
    params.limit,
    view,
  ];
  const infiniteQuery = useGenericInfiniteQuery<EventListItem[], RequestError>(
    queryParams,
    ({ pageParam }) => {
      let offset = 0;
      const initialLimit = params.limit ?? 10;
      if (pageParam !== 0) {
        offset = pageParam === 1 ? initialLimit : (pageParam - 1) * 10 + initialLimit;
      }
      return getEventList({ companyId, clubId, params: { ...params, offset: offset || 0 } });
    },
    {
      ...options,
      initialPageParam: 0,
      getNextPageParam: (lastPage: EventListItem[], allPages: EventListItem[][]) => {
        if (
          (allPages.length > 1 && lastPage.length < 10) ||
          (allPages.length === 1 && lastPage.length < (params.limit || 10))
        ) {
          return undefined;
        }
        return allPages.length;
      },
    }
  );
  return infiniteQuery;
};

export const useGetSimpleEventList = ({ companyId, params, options, clubId, view }: GetEventListHookParams) => {
  const queryParams = [
    `${QUERY_KEYS.eventList}-${view}`,
    companyId,
    clubId,
    params.search,
    params.sort,
    params.orderBy,
    params.startingWeekDays,
    params.endingTimeFrom,
    params.endingTimeUntil,
    params.startTime,
    params.endTime,
    // Part of the key because it bounds the rows returned: without it a consumer
    // asking for 100 can be served another's cached page of 10.
    params.limit,
    view,
  ];
  const query = useGenericQuery<EventListItem[]>(
    queryParams,
    () => getEventList({ companyId, clubId, params }),
    {
      ...options,
    },
    false
  );
  return query;
};
